import { memo } from 'react'
import { Link } from 'react-router'
import {
  Search,
  Upload,
  Eye,
  Archive,
  FileText,
  Download,
  ArchiveRestore,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { TableSkeleton } from '@/components/loading'
import { EmptyState } from '@/components/shared'
import { QueryError } from '@/components/error'
import { UploadDocumentDialog, EditDocumentDialog } from '@/components/features/documents'
import type { Document, GeneratedDocument } from '@/types'
import { useDocumentsPage } from './DocumentsPage.logic'
import { formatFileSize } from './DocumentsPage.static'

export function DocumentsPage() {
  const {
    t,
    uploadedDocs,
    generatedDocs,
    inputValue,
    search,
    showArchive,
    uploadDialogOpen,
    deleteTargetId,
    isLoadingUploaded,
    isLoadingGenerated,
    isErrorUploaded,
    isErrorGenerated,
    uploadedPage,
    uploadedTotalPages,
    uploadedTotal,
    generatedPage,
    generatedTotalPages,
    generatedTotal,
    setUploadedPage,
    setGeneratedPage,
    setInputValue,
    handleSearchKeyDown,
    setUploadDialogOpen,
    handleToggleArchive,
    handleDownloadUploaded,
    handleArchiveUploaded,
    handleUnarchiveUploaded,
    openDeleteUploaded,
    handleDownloadGenerated,
    handleArchiveGenerated,
    handleUnarchiveGenerated,
    openDeleteGenerated,
    closeDelete,
    handleConfirmDelete,
    editTargetDoc,
    setEditTargetDoc,
  } = useDocumentsPage()

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">
            {showArchive ? t('archiveDescription') : t('description')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={showArchive ? 'default' : 'outline'} onClick={handleToggleArchive}>
            <Archive className="mr-2 size-4" />
            {t('archive')}
          </Button>
          <Button onClick={() => setUploadDialogOpen(true)}>
            <Upload className="mr-2 size-4" />
            {t('uploadDocument')}
          </Button>
        </div>
      </div>

      <div className="relative min-w-0 max-w-sm">
        <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
        <Input
          placeholder={t('searchPlaceholder')}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          className="pl-9"
        />
      </div>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t('uploadedDocuments')}</h2>
        {isLoadingUploaded ? (
          <TableSkeleton rows={6} columns={5} />
        ) : isErrorUploaded ? (
          <QueryError />
        ) : uploadedDocs.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={search ? t('noSearchResults', { search }) : t('noDocuments')}
            description={search ? t('noSearchResultsDescription') : t('noDocumentsDescription')}
          />
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('columns.title')}</TableHead>
                  <TableHead>{t('columns.type')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('columns.filename')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('columns.size')}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t('columns.uploadDate')}</TableHead>
                  <TableHead className="w-[120px]">{t('columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uploadedDocs.map((doc) => (
                  <UploadedDocRow
                    key={doc.id}
                    doc={doc}
                    t={t}
                    onDownload={handleDownloadUploaded}
                    onArchive={handleArchiveUploaded}
                    onUnarchive={handleUnarchiveUploaded}
                    onDelete={openDeleteUploaded}
                    onEdit={setEditTargetDoc}
                  />
                ))}
              </TableBody>
            </Table>
            {uploadedTotalPages > 1 && (
              <div className="flex flex-col items-center justify-between gap-2 border-t px-4 py-3 sm:flex-row">
                <p className="text-muted-foreground text-sm">
                  {t('pagination.showing', {
                    count: uploadedTotal,
                    page: uploadedPage,
                    totalPages: uploadedTotalPages,
                  })}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUploadedPage(uploadedPage - 1)}
                    disabled={uploadedPage <= 1}
                  >
                    <ChevronLeft className="mr-1 size-4" />
                    {t('pagination.previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUploadedPage(uploadedPage + 1)}
                    disabled={uploadedPage >= uploadedTotalPages}
                  >
                    {t('pagination.next')}
                    <ChevronRight className="ml-1 size-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t('generatedDocuments')}</h2>
        {isLoadingGenerated ? (
          <TableSkeleton rows={6} columns={5} />
        ) : isErrorGenerated ? (
          <QueryError />
        ) : generatedDocs.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={search ? t('noSearchResults', { search }) : t('noGeneratedDocuments')}
            description={search ? t('noSearchResultsDescription') : t('noDocumentsDescription')}
          />
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('patient')}</TableHead>
                  <TableHead>{t('columns.template')}</TableHead>
                  <TableHead>{t('columns.status')}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t('columns.uploadDate')}</TableHead>
                  <TableHead className="w-[140px]">{t('columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {generatedDocs.map((doc) => (
                  <GeneratedDocRow
                    key={doc.id}
                    doc={doc}
                    t={t}
                    onDownload={handleDownloadGenerated}
                    onArchive={handleArchiveGenerated}
                    onUnarchive={handleUnarchiveGenerated}
                    onDelete={openDeleteGenerated}
                  />
                ))}
              </TableBody>
            </Table>
            {generatedTotalPages > 1 && (
              <div className="flex flex-col items-center justify-between gap-2 border-t px-4 py-3 sm:flex-row">
                <p className="text-muted-foreground text-sm">
                  {t('pagination.showing', {
                    count: generatedTotal,
                    page: generatedPage,
                    totalPages: generatedTotalPages,
                  })}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setGeneratedPage(generatedPage - 1)}
                    disabled={generatedPage <= 1}
                  >
                    <ChevronLeft className="mr-1 size-4" />
                    {t('pagination.previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setGeneratedPage(generatedPage + 1)}
                    disabled={generatedPage >= generatedTotalPages}
                  >
                    {t('pagination.next')}
                    <ChevronRight className="ml-1 size-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <UploadDocumentDialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} />

      {editTargetDoc && (
        <EditDocumentDialog
          open={!!editTargetDoc}
          onClose={() => setEditTargetDoc(null)}
          document={editTargetDoc}
        />
      )}

      <AlertDialog open={!!deleteTargetId} onOpenChange={closeDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteDocument')}</AlertDialogTitle>
            <AlertDialogDescription>{t('confirmDeleteDocument')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

const UploadedDocRow = memo(function UploadedDocRow({
  doc,
  t,
  onDownload,
  onArchive,
  onUnarchive,
  onDelete,
  onEdit,
}: {
  doc: Document
  t: ReturnType<typeof useDocumentsPage>['t']
  onDownload: (id: string) => void
  onArchive: (id: string) => void
  onUnarchive: (id: string) => void
  onDelete: (id: string) => void
  onEdit: (doc: Document) => void
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">{doc.title}</TableCell>
      <TableCell>
        <Badge variant="secondary">
          {t(`types.${doc.documentType.toLowerCase()}` as 'types.other')}
        </Badge>
      </TableCell>
      <TableCell className="hidden max-w-[200px] truncate md:table-cell">
        {doc.originalFilename}
      </TableCell>
      <TableCell className="hidden md:table-cell">{formatFileSize(doc.fileSizeBytes)}</TableCell>
      <TableCell className="hidden lg:table-cell">
        {new Date(doc.createdAt).toLocaleDateString('bg-BG')}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" asChild title={t('viewDocument')}>
            <Link to={`/documents/${doc.id}`}>
              <Eye className="size-4" />
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDownload(doc.id)}
            title={t('downloadDocument')}
          >
            <Download className="size-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onEdit(doc)} title={t('editDocument')}>
            <Pencil className="size-4" />
          </Button>
          {doc.isArchived ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onUnarchive(doc.id)}
              title={t('unarchiveDocument')}
            >
              <ArchiveRestore className="size-4" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onArchive(doc.id)}
              title={t('archiveDocument')}
            >
              <Archive className="size-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => onDelete(doc.id)}
            title={t('deleteDocument')}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
})

const GeneratedDocRow = memo(function GeneratedDocRow({
  doc,
  t,
  onDownload,
  onArchive,
  onUnarchive,
  onDelete,
}: {
  doc: GeneratedDocument
  t: ReturnType<typeof useDocumentsPage>['t']
  onDownload: (id: string) => void
  onArchive: (id: string) => void
  onUnarchive: (id: string) => void
  onDelete: (id: string) => void
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">{doc.patientName}</TableCell>
      <TableCell>
        <Badge variant="outline">
          {t(`templates.${doc.templateCode}` as 'templates.DOSIE', doc.templateCode)}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant="secondary">
          {t(`status.${doc.status.toLowerCase()}` as 'status.generated')}
        </Badge>
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {new Date(doc.createdAt).toLocaleDateString('bg-BG')}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDownload(doc.id)}
            title={t('downloadDocument')}
          >
            <Download className="size-4" />
          </Button>
          {doc.status === 'ARCHIVED' ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onUnarchive(doc.id)}
              title={t('unarchiveDocument')}
            >
              <ArchiveRestore className="size-4" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onArchive(doc.id)}
              title={t('archiveDocument')}
            >
              <Archive className="size-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => onDelete(doc.id)}
            title={t('deleteDocument')}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
})
